import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input, Label, Textarea, Select } from '../ui/input';
import { http } from '../../lib/api';
import GestionPermisos from './GestionPermisos';
import Integraciones from './integraciones/Integraciones';
import AppriseRoutingConfig from './notificaciones/AppriseRoutingConfig';
import PanelWhatsApp from './whatsapp/PanelWhatsApp';
import ClientesV3Preview from './ClientesV3Preview';
import { 
  Settings, 
  Database, 
  FileText, 
  Bell, 
  BarChart3, 
  Zap, 
  Globe, 
  Layout,
  Download,
  Upload,
  Save,
  RefreshCw,
  Play,
  Pause,
  Trash2,
  Plus,
  Puzzle,
  Edit,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock,
  Users,
  Activity,
  Building,
  Shield,
  Mail,
  MessageSquare,
  Wrench
} from 'lucide-react';

// ─── Email History Panel ──────────────────────────────────────────────────────
const EmailHistoryPanel = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterEmail, setFilterEmail] = useState('');
  const [filterOrden, setFilterOrden] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testMsg, setTestMsg] = useState(null);
  const [sending, setSending] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 200 });
      if (filterEmail) params.append('email', filterEmail);
      if (filterOrden) params.append('orden_numero', filterOrden);
      const res = await http.get(`/admin/email-logs?${params}`);
      setLogs(res.data || []);
    } catch (e) {
      console.error('Error cargando email logs:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []);

  const handleTest = async () => {
    if (!testEmail) return;
    setSending(true);
    setTestMsg(null);
    try {
      const res = await http.post('/admin/email-test', { email: testEmail });
      setTestMsg({ ok: true, text: res.data.message });
      fetchLogs();
    } catch (e) {
      setTestMsg({ ok: false, text: e.response?.data?.detail || 'Error al enviar' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center">
            <Mail className="mr-2 h-5 w-5 text-emerald-500" />
            Historial de Correos Enviados
          </h3>
          <button
            onClick={fetchLogs}
            className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <Input
            className="w-52"
            placeholder="Filtrar por email..."
            value={filterEmail}
            onChange={e => setFilterEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchLogs()}
          />
          <Input
            className="w-40"
            placeholder="N° Orden..."
            value={filterOrden}
            onChange={e => setFilterOrden(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchLogs()}
          />
          <button
            onClick={fetchLogs}
            className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
          >
            Buscar
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-sm text-gray-500 py-4 text-center">Cargando...</p>
        ) : logs.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">No hay registros de correos enviados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-gray-500 uppercase">
                  <th className="pb-2 pr-3">Fecha</th>
                  <th className="pb-2 pr-3">Destinatario</th>
                  <th className="pb-2 pr-3">Asunto</th>
                  <th className="pb-2 pr-3">Orden</th>
                  <th className="pb-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2 pr-3 whitespace-nowrap text-gray-500 text-xs">
                      {log.sent_at ? new Date(log.sent_at).toLocaleString('es-PY') : '—'}
                    </td>
                    <td className="py-2 pr-3 text-gray-800">{log.to || '—'}</td>
                    <td className="py-2 pr-3 text-gray-600 max-w-xs truncate" title={log.subject}>{log.subject || '—'}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{log.orden_numero || '—'}</td>
                    <td className="py-2">
                      {log.success ? (
                        <span className="inline-flex items-center gap-1 text-green-600 bg-green-50 px-2 py-0.5 rounded-full text-xs font-medium">
                          <CheckCircle className="h-3 w-3" /> Enviado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600 bg-red-50 px-2 py-0.5 rounded-full text-xs font-medium" title={log.error || ''}>
                          <AlertCircle className="h-3 w-3" /> Error
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-gray-400 mt-2 text-right">{logs.length} registro(s)</p>
          </div>
        )}

        {/* Test email */}
        <div className="mt-5 border-t pt-4">
          <p className="text-sm font-medium mb-2">Enviar email de prueba</p>
          <div className="flex gap-2 items-center">
            <Input
              className="w-64"
              type="email"
              placeholder="destinatario@ejemplo.com"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleTest()}
            />
            <button
              onClick={handleTest}
              disabled={sending || !testEmail}
              className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {sending ? 'Enviando...' : 'Enviar prueba'}
            </button>
          </div>
          {testMsg && (
            <p className={`mt-2 text-sm ${testMsg.ok ? 'text-green-600' : 'text-red-600'}`}>
              {testMsg.text}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
};

const PanelAvanzado = () => {
  const [activeTab, setActiveTab] = useState('configuracion');
  const [loading, setLoading] = useState(false);
  
  // Estados para métricas reales
  const [metricsData, setMetricsData] = useState(null);
  const [logsData, setLogsData] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  
  const [systemConfig, setSystemConfig] = useState({
    empresa: {
      nombre: 'Elements',
      email: 'info@elements.com.py',
      telefono: '+595 21 123456',
      direccion: 'Asunción, Paraguay'
    },
    seguridad: {
      sessionTimeout: 30,
      maxLoginAttempts: 3,
      passwordMinLength: 8
    },
    mantenimiento: {
      autoBackup: true,
      cleanupDays: 30,
      logRetention: 90
    }
  });

  // Cargar métricas del backend al montar o cambiar de tab
  useEffect(() => {
    if (activeTab === 'metricas') {
      loadMetrics();
      loadSystemHealth();
    } else if (activeTab === 'logs') {
      loadLogs();
    } else if (activeTab === 'configuracion') {
      loadConfig();
    } else if (activeTab === 'backups') {
      loadBackups();
    }
  }, [activeTab]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await http.get('/admin/advanced/config');
      setSystemConfig(response.data);
    } catch (error) {
      console.error('Error cargando configuración:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBackups = async () => {
    setLoading(true);
    try {
      const response = await http.get('/admin/advanced/backups');
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error cargando backups:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const response = await http.get('/admin/advanced/dashboard/executive');
      setMetricsData(response.data);
    } catch (error) {
      console.error('Error cargando métricas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSystemHealth = async () => {
    try {
      const response = await http.get('/admin/advanced/system/health');
      const d = response.data;
      setSystemHealth({
        cpu_percent: d.recursos?.cpu?.porcentaje_uso,
        memory_percent: d.recursos?.memoria?.porcentaje_uso,
        disk_percent: d.recursos?.disco?.porcentaje_uso,
        database: {
          status: d.servicios?.base_datos?.estado === 'conectada' ? 'connected' : 'error',
          collections: d.servicios?.base_datos?.stats?.collections,
        }
      });
    } catch (error) {
      console.error('Error cargando salud del sistema:', error);
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const response = await http.get('/admin/advanced/logs?limit=100');
      setLogsData(response.data.logs || []);
    } catch (error) {
      console.error('Error cargando logs:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const [backups, setBackups] = useState([
    {
      id: 1,
      fecha: '2025-10-05 15:30',
      tipo: 'Automático',
      tamaño: '125 MB',
      estado: 'Completado'
    },
    {
      id: 2,
      fecha: '2025-10-04 15:30',
      tipo: 'Manual',
      tamaño: '123 MB',
      estado: 'Completado'
    }
  ]);

  // logs ahora se cargan desde el backend mediante logsData
  
  const [notifications, setNotifications] = useState({
    email: {
      enabled: true,
      smtp: 'smtp.gmail.com',
      port: 587,
      username: '',
      password: ''
    },
    sms: {
      enabled: false,
      provider: '',
      apiKey: ''
    },
    push: {
      enabled: false,
      firebaseKey: ''
    }
  });

  const handleSaveConfig = async () => {
    setLoading(true);
    try {
      await http.post('/admin/advanced/config', systemConfig);
      alert('✅ Configuración guardada exitosamente');
      await loadConfig(); // Recargar para confirmar
    } catch (error) {
      console.error('Error al guardar la configuración:', error);
      alert(`❌ Error al guardar: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    if (!window.confirm('¿Crear un nuevo backup de la base de datos? Esto puede tardar unos minutos.')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await http.post('/admin/advanced/backups/create');
      alert(`✅ ${response.data.message}\nArchivo: ${response.data.backup.filename}\nTamaño: ${response.data.backup.tamaño}`);
      await loadBackups(); // Recargar lista
    } catch (error) {
      console.error('Error al crear backup:', error);
      alert(`❌ Error al crear backup: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(`¿Eliminar el backup "${filename}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    setLoading(true);
    try {
      await http.delete(`/admin/advanced/backups/${filename}`);
      alert('✅ Backup eliminado exitosamente');
      await loadBackups(); // Recargar lista
    } catch (error) {
      console.error('Error al eliminar backup:', error);
      alert(`❌ Error al eliminar: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshLogs = async () => {
    setLoading(true);
    try {
      await loadLogs();
    } catch (error) {
      console.error('Error refrescando logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      id: 'configuracion',
      label: 'Configuración',
      icon: Settings,
      description: 'Configuraciones generales del sistema'
    },
    {
      id: 'permisos',
      label: 'Permisos',
      icon: Shield,
      description: 'Gestión de acceso a módulos'
    },
    {
      id: 'clientes_v3',
      label: 'Clientes V3',
      icon: Users,
      description: 'Preview aislado del módulo clientes con nuevo estilo'
    },
    {
      id: 'backups',
      label: 'Backups',
      icon: Database,
      description: 'Copias de seguridad y restauración'
    },
    {
      id: 'logs',
      label: 'Logs',
      icon: FileText,
      description: 'Registros y auditoría'
    },
    {
      id: 'notificaciones',
      label: 'Notificaciones',
      icon: Bell,
      description: 'Email, SMS y notificaciones push'
    },
    {
      id: 'metricas',
      label: 'Métricas',
      icon: BarChart3,
      description: 'Analytics y reportes detallados'
    },
    {
      id: 'automatizaciones',
      label: 'Automatiz.',
      icon: Zap,
      description: 'Workflows y reglas de negocio'
    },
    {
      id: 'integraciones',
      label: 'Integraciones',
      icon: Globe,
      description: 'APIs y servicios externos'
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageSquare,
      description: 'Bot de WhatsApp para alertas del sistema'
    },
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Layout,
      description: 'Widgets y métricas personalizadas'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'permisos':
        return <GestionPermisos />;

      case 'clientes_v3':
        return <ClientesV3Preview />;
        
      case 'configuracion':
        return (
          <div className="space-y-6">
            <Card>
              <div className="p-6 bg-gradient-to-r from-emerald-50 to-purple-50 rounded-xl border border-emerald-200">
                <h3 className="text-lg font-semibold mb-4 flex items-center text-emerald-900">
                  <Building className="mr-2 h-5 w-5" />
                  Información de la Empresa
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Nombre de la empresa</label>
                    <Input
                      value={systemConfig.empresa.nombre}
                      onChange={(e) => setSystemConfig({
                        ...systemConfig,
                        empresa: { ...systemConfig.empresa, nombre: e.target.value }
                      })}
                      className="rounded-lg border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Email</label>
                    <Input
                      type="email"
                      value={systemConfig.empresa.email}
                      onChange={(e) => setSystemConfig({
                        ...systemConfig,
                        empresa: { ...systemConfig.empresa, email: e.target.value }
                      })}
                      className="rounded-lg border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Teléfono</label>
                    <Input
                      value={systemConfig.empresa.telefono}
                      onChange={(e) => setSystemConfig({
                        ...systemConfig,
                        empresa: { ...systemConfig.empresa, telefono: e.target.value }
                      })}
                      className="rounded-lg border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Dirección</label>
                    <Input
                      value={systemConfig.empresa.direccion}
                      onChange={(e) => setSystemConfig({
                        ...systemConfig,
                        empresa: { ...systemConfig.empresa, direccion: e.target.value }
                      })}
                      className="rounded-lg border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                    />
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <h3 className="text-lg font-semibold mb-4 flex items-center text-green-900">
                  <Shield className="mr-2 h-5 w-5" />
                  Configuración de Seguridad
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Timeout de sesión (min)</label>
                    <Input
                      type="number"
                      value={systemConfig.seguridad.sessionTimeout}
                      onChange={(e) => setSystemConfig({
                        ...systemConfig,
                        seguridad: { ...systemConfig.seguridad, sessionTimeout: parseInt(e.target.value) }
                      })}
                      className="rounded-lg border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Intentos máximos de login</label>
                    <Input
                      type="number"
                      value={systemConfig.seguridad.maxLoginAttempts}
                      onChange={(e) => setSystemConfig({
                        ...systemConfig,
                        seguridad: { ...systemConfig.seguridad, maxLoginAttempts: parseInt(e.target.value) }
                      })}
                      className="rounded-lg border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Longitud mínima contraseña</label>
                    <Input
                      type="number"
                      value={systemConfig.seguridad.passwordMinLength}
                      onChange={(e) => setSystemConfig({
                        ...systemConfig,
                        seguridad: { ...systemConfig.seguridad, passwordMinLength: parseInt(e.target.value) }
                      })}
                      className="rounded-lg border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200"
                    />
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200">
                <h3 className="text-lg font-semibold mb-4 flex items-center text-orange-900">
                  <Settings className="mr-2 h-5 w-5" />
                  Configuración de Mantenimiento
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={systemConfig.mantenimiento.autoBackup}
                      onChange={(e) => setSystemConfig({
                        ...systemConfig,
                        mantenimiento: { ...systemConfig.mantenimiento, autoBackup: e.target.checked }
                      })}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-200"
                    />
                    <label className="text-sm font-medium text-gray-700">Backup automático diario</label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">Días para limpiar archivos temp</label>
                      <Input
                        type="number"
                        value={systemConfig.mantenimiento.cleanupDays}
                        onChange={(e) => setSystemConfig({
                          ...systemConfig,
                          mantenimiento: { ...systemConfig.mantenimiento, cleanupDays: parseInt(e.target.value) }
                        })}
                        className="rounded-lg border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">Retención de logs (días)</label>
                      <Input
                        type="number"
                        value={systemConfig.mantenimiento.logRetention}
                        onChange={(e) => setSystemConfig({
                          ...systemConfig,
                          mantenimiento: { ...systemConfig.mantenimiento, logRetention: parseInt(e.target.value) }
                        })}
                        className="rounded-lg border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <div className="flex justify-end">
              <Button 
                onClick={handleSaveConfig} 
                disabled={loading}
                className="bg-gradient-to-r from-emerald-600 to-purple-600 hover:from-emerald-700 hover:to-purple-700 text-white px-8 py-3 rounded-lg shadow-lg transform transition-all duration-200 hover:scale-105"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Save className="mr-2 h-4 w-4" />
                    Guardar Configuración
                  </div>
                )}
              </Button>
            </div>
          </div>
        );

      case 'backups':
        return (
          <div className="space-y-6">
            <Card>
              <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold flex items-center text-emerald-900">
                    <Download className="mr-2 h-5 w-5" />
                    Gestión de Backups
                  </h3>
                  <Button 
                    onClick={handleCreateBackup} 
                    disabled={loading}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg transform transition-all duration-200 hover:scale-105"
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creando...
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <Upload className="mr-2 h-4 w-4" />
                        Crear Backup
                      </div>
                    )}
                  </Button>
                </div>

                <div className="overflow-hidden shadow-lg rounded-xl">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Fecha</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Tipo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Tamaño</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Estado</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {backups.length > 0 ? (
                        backups.map((backup, index) => (
                          <tr key={backup.filename || backup.id} className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{backup.fecha}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                backup.tipo === 'Automático' 
                                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                                  : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                              }`}>
                                {backup.tipo}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{backup.tamaño}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                {backup.estado}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <div className="flex space-x-2">
                                <Button 
                                  size="sm" 
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-1 rounded-lg shadow-sm transform transition-all duration-200 hover:scale-105"
                                  onClick={() => alert(`Descarga manual desde: ${backup.filename}`)}
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  Descargar
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1 rounded-lg shadow-sm transform transition-all duration-200 hover:scale-105"
                                  onClick={() => handleDeleteBackup(backup.filename || backup.id)}
                                  disabled={loading}
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Eliminar
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                            {loading ? 'Cargando backups...' : 'No hay backups disponibles. Crea uno usando el botón "Crear Backup".'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'logs':
        return (
          <div className="space-y-6">
            <Card>
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold flex items-center">
                    <FileText className="mr-2 h-5 w-5" />
                    Logs del Sistema
                  </h3>
                  <Button 
                    onClick={refreshLogs} 
                    disabled={loading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Actualizando...
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Actualizar
                      </div>
                    )}
                  </Button>
                </div>

                <div className="bg-black text-green-400 p-4 rounded-lg font-mono text-sm max-h-96 overflow-y-auto">
                  {logsData.length > 0 ? (
                    logsData.map((log, index) => (
                      <div key={index} className="mb-1">
                        <span className="text-gray-400">[{log.timestamp}]</span>{' '}
                        <span className={`font-bold ${
                          log.nivel === 'ERROR' ? 'text-red-400' :
                          log.nivel === 'WARNING' || log.nivel === 'WARN' ? 'text-yellow-400' :
                          log.nivel === 'DEBUG' ? 'text-emerald-400' :
                          'text-green-400'
                        }`}>
                          {log.nivel}
                        </span>{' '}
                        <span className="text-cyan-400">{log.modulo || 'SYSTEM'}</span>{' '}
                        <span className="text-white">{log.mensaje}</span>
                        {log.ip && <span className="text-gray-500"> ({log.ip})</span>}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      {loading ? 'Cargando logs...' : 'No hay logs disponibles'}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        );

      case 'notificaciones':
        return (
          <div className="space-y-6">
            <EmailHistoryPanel />
            <AppriseRoutingConfig />
            
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Bell className="mr-2 h-5 w-5" />
                  Configuración Legacy (Email / SMS)
                </h3>

                <div className="space-y-6">
                  {/* Email */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium flex items-center">
                        <Mail className="mr-2 h-4 w-4" />
                        Notificaciones por Email
                      </h4>
                      <input
                        type="checkbox"
                        checked={notifications.email.enabled}
                        onChange={(e) => setNotifications({
                          ...notifications,
                          email: { ...notifications.email, enabled: e.target.checked }
                        })}
                        className="rounded"
                      />
                    </div>
                    {notifications.email.enabled && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Servidor SMTP</label>
                          <Input
                            value={notifications.email.smtp}
                            onChange={(e) => setNotifications({
                              ...notifications,
                              email: { ...notifications.email, smtp: e.target.value }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Puerto</label>
                          <Input
                            type="number"
                            value={notifications.email.port}
                            onChange={(e) => setNotifications({
                              ...notifications,
                              email: { ...notifications.email, port: parseInt(e.target.value) }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Usuario</label>
                          <Input
                            value={notifications.email.username}
                            onChange={(e) => setNotifications({
                              ...notifications,
                              email: { ...notifications.email, username: e.target.value }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Contraseña</label>
                          <Input
                            type="password"
                            value={notifications.email.password}
                            onChange={(e) => setNotifications({
                              ...notifications,
                              email: { ...notifications.email, password: e.target.value }
                            })}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* SMS */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium flex items-center">
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Notificaciones por SMS
                      </h4>
                      <input
                        type="checkbox"
                        checked={notifications.sms.enabled}
                        onChange={(e) => setNotifications({
                          ...notifications,
                          sms: { ...notifications.sms, enabled: e.target.checked }
                        })}
                        className="rounded"
                      />
                    </div>
                    {notifications.sms.enabled && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Proveedor SMS</label>
                          <select 
                            value={notifications.sms.provider}
                            onChange={(e) => setNotifications({
                              ...notifications,
                              sms: { ...notifications.sms, provider: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          >
                            <option value="">Seleccionar proveedor</option>
                            <option value="twilio">Twilio</option>
                            <option value="nexmo">Nexmo</option>
                            <option value="local">Proveedor Local</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">API Key</label>
                          <Input
                            type="password"
                            value={notifications.sms.apiKey}
                            onChange={(e) => setNotifications({
                              ...notifications,
                              sms: { ...notifications.sms, apiKey: e.target.value }
                            })}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button 
                    onClick={() => alert('Configuración de notificaciones guardada')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Guardar Configuración
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'metricas':
        return (
          <div className="space-y-6">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : (
              <>
                {/* Métricas Ejecutivas */}
                {metricsData && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card>
                      <div className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Total Clientes</p>
                            <p className="text-2xl font-bold text-emerald-600">
                              {metricsData.resumen_ejecutivo?.total_clientes || 0}
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Users className="h-6 w-6 text-emerald-600" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Activos: {metricsData.resumen_ejecutivo?.clientes_activos || 0}
                        </p>
                      </div>
                    </Card>

                    <Card>
                      <div className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Órdenes del Mes</p>
                            <p className="text-2xl font-bold text-green-600">
                              {metricsData.resumen_ejecutivo?.ordenes_mes || 0}
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                            <FileText className="h-6 w-6 text-green-600" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Completadas: {metricsData.resumen_ejecutivo?.ordenes_completadas_mes || 0} 
                          ({metricsData.resumen_ejecutivo?.tasa_completacion || 0}%)
                        </p>
                      </div>
                    </Card>

                    <Card>
                      <div className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Horas Trabajadas</p>
                            <p className="text-2xl font-bold text-orange-600">
                              {metricsData.resumen_ejecutivo?.total_horas_mes?.toFixed(1) || 0}h
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                            <Clock className="h-6 w-6 text-orange-600" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Este mes</p>
                      </div>
                    </Card>

                    <Card>
                      <div className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Ingresos Estimados</p>
                            <p className="text-2xl font-bold text-purple-600">
                              ${(metricsData.resumen_ejecutivo?.ingresos_estimados_mes || 0).toLocaleString()}
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Activity className="h-6 w-6 text-purple-600" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Proyección mensual</p>
                      </div>
                    </Card>
                  </div>
                )}

                {/* Salud del Sistema */}
                {systemHealth && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card>
                      <div className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">CPU</p>
                            <p className="text-2xl font-bold text-emerald-600">
                              {systemHealth.cpu_percent?.toFixed(1)}%
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Activity className="h-6 w-6 text-emerald-600" />
                          </div>
                        </div>
                        <div className="mt-4">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-emerald-600 h-2 rounded-full" 
                              style={{width: `${systemHealth.cpu_percent || 0}%`}}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </Card>

                    <Card>
                      <div className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Memoria</p>
                            <p className="text-2xl font-bold text-green-600">
                              {systemHealth.memory_percent?.toFixed(1)}%
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                            <Database className="h-6 w-6 text-green-600" />
                          </div>
                        </div>
                        <div className="mt-4">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full" 
                              style={{width: `${systemHealth.memory_percent || 0}%`}}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </Card>

                    <Card>
                      <div className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Almacenamiento</p>
                            <p className="text-2xl font-bold text-orange-600">
                              {systemHealth.disk_percent?.toFixed(1)}%
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                            <Download className="h-6 w-6 text-orange-600" />
                          </div>
                        </div>
                        <div className="mt-4">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-orange-600 h-2 rounded-full" 
                              style={{width: `${systemHealth.disk_percent || 0}%`}}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </Card>

                    <Card>
                      <div className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">MongoDB</p>
                            <p className="text-2xl font-bold text-purple-600">
                              {systemHealth.database?.status === 'connected' ? 'OK' : 'Error'}
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Database className="h-6 w-6 text-purple-600" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-4">
                          {systemHealth.database?.collections || 0} colecciones
                        </p>
                      </div>
                    </Card>
                  </div>
                )}

                {/* Top Clientes y Técnicos */}
                {metricsData && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <div className="p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center">
                          <Users className="mr-2 h-5 w-5" />
                          Top Clientes del Mes
                        </h3>
                        <div className="space-y-3">
                          {metricsData.top_clientes_mes?.map((cliente, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="font-medium">{cliente.cliente}</span>
                              <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm">
                                {cliente.ordenes} órdenes
                              </span>
                            </div>
                          ))}
                          {(!metricsData.top_clientes_mes || metricsData.top_clientes_mes.length === 0) && (
                            <p className="text-gray-500 text-center py-4">No hay datos disponibles</p>
                          )}
                        </div>
                      </div>
                    </Card>

                    <Card>
                      <div className="p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center">
                          <Activity className="mr-2 h-5 w-5" />
                          Técnicos Más Activos
                        </h3>
                        <div className="space-y-3">
                          {metricsData.top_tecnicos_mes?.map((tecnico, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="font-medium">{tecnico.tecnico}</span>
                              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                                {tecnico.ordenes} órdenes
                              </span>
                            </div>
                          ))}
                          {(!metricsData.top_tecnicos_mes || metricsData.top_tecnicos_mes.length === 0) && (
                            <p className="text-gray-500 text-center py-4">No hay datos disponibles</p>
                          )}
                        </div>
                      </div>
                    </Card>
                  </div>
                )}

                {/* Botón de actualizar */}
                <div className="flex justify-end">
                  <Button 
                    onClick={() => { loadMetrics(); loadSystemHealth(); }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Actualizar Métricas
                  </Button>
                </div>
              </>
            )}
          </div>
        );

      case 'automatizaciones':
        return (
          <div className="space-y-6">
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Zap className="mr-2 h-5 w-5" />
                  Automatizaciones del Sistema
                </h3>

                <div className="space-y-4">
                  <div className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Backup Automático Diario</h4>
                          <p className="text-sm text-gray-500">Se ejecuta a las 02:00 AM</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Activo</span>
                        <Button size="sm" className="bg-gray-100 text-gray-700 hover:bg-gray-200">
                          <Edit className="h-3 w-3 mr-1" />
                          Editar
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Clock className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Limpieza de Logs</h4>
                          <p className="text-sm text-gray-500">Limpia logs antiguos cada semana</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Activo</span>
                        <Button size="sm" className="bg-gray-100 text-gray-700 hover:bg-gray-200">
                          <Edit className="h-3 w-3 mr-1" />
                          Editar
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <AlertCircle className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Alertas de Mantenimiento</h4>
                          <p className="text-sm text-gray-500">Notifica equipos que requieren mantenimiento</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">Inactivo</span>
                        <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700">
                          <Play className="h-3 w-3 mr-1" />
                          Activar
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <Button className="bg-green-600 hover:bg-green-700 text-white">
                    <Plus className="mr-2 h-4 w-4" />
                    Nueva Automatización
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'integraciones':
        return <Integraciones />;

      case 'whatsapp':
        return <PanelWhatsApp />;

      case 'dashboard':
        return (
          <div className="space-y-6">
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Layout className="mr-2 h-5 w-5" />
                  Dashboard Personalizable
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-3">Widgets Disponibles</h4>
                    <div className="space-y-2">
                      {[
                        'Órdenes Recientes',
                        'Estadísticas de Técnicos', 
                        'Equipos por Mantenimiento',
                        'Ingresos Mensuales',
                        'Clientes Activos',
                        'Alertas del Sistema'
                      ].map((widget, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                          <span className="text-sm">{widget}</span>
                          <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700 text-xs px-2 py-1">
                            <Plus className="h-3 w-3 mr-1" />
                            Agregar
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-3">Vista Previa del Dashboard</h4>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg h-64 flex items-center justify-center">
                      <div className="text-center">
                        <Layout className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500">Arrastra widgets aquí para personalizar</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex space-x-3">
                  <Button className="bg-green-600 hover:bg-green-700 text-white">
                    <Save className="mr-2 h-4 w-4" />
                    Guardar Layout
                  </Button>
                  <Button className="bg-gray-100 text-gray-700 hover:bg-gray-200">
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Restaurar por Defecto
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        );

      default:
        return (
          <Card>
            <div className="p-8 text-center bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200">
              <div className="flex flex-col items-center space-y-4">
                <div className="bg-gradient-to-r from-emerald-500 to-purple-500 p-4 rounded-full shadow-lg">
                  <Wrench className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Panel en Desarrollo</h3>
                <p className="text-gray-600 max-w-md leading-relaxed">
                  Esta funcionalidad está siendo desarrollada. Pronto estará disponible con 
                  todas las características avanzadas para la administración del sistema.
                </p>
                <div className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-lg">
                  Módulo: <span className="font-mono font-semibold text-emerald-600">{activeTab}</span>
                </div>
              </div>
            </div>
          </Card>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header mejorado */}
        <div className="mb-8 bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-purple-600 bg-clip-text text-transparent">
                Panel Avanzado
              </h1>
              <p className="text-gray-600 mt-1">
                Herramientas avanzadas de administración y configuración del sistema
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="h-12 w-12 bg-gradient-to-r from-emerald-500 to-purple-500 rounded-xl flex items-center justify-center">
                <Settings className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs mejoradas */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
          <nav className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-11 divide-x divide-gray-100">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  title={tab.description}
                  className={`relative flex flex-col items-center gap-1.5 py-3 px-1 text-xs font-medium transition-all duration-150 group
                    ${isActive
                      ? 'bg-gradient-to-b from-emerald-50 to-purple-50 text-emerald-700'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                    }`}
                >
                  {/* Indicador activo en la parte superior */}
                  <span className={`absolute top-0 left-0 right-0 h-0.5 rounded-b transition-all duration-200
                    ${isActive ? 'bg-gradient-to-r from-emerald-500 to-purple-500' : 'bg-transparent'}`}
                  />
                  <Icon className={`h-5 w-5 transition-transform duration-150 ${isActive ? 'text-emerald-600 scale-110' : 'group-hover:scale-105'}`} />
                  <span className={`leading-tight text-center ${isActive ? 'text-emerald-700 font-semibold' : ''}`}>
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content con mejor espaciado */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default PanelAvanzado;
